import { computed, Ref } from '@vue/composition-api';
import { useOfflineEventsList } from './useOfflineEventsList';
import { i18n } from './../../../../i18n';
import { useCreativeActionTypeIs, useRedDot } from '@tt4b/ttam-hooks';
import { AcoFormData, ACTION_TYPE, AdgroupFormData, FullNormalFormData } from '@tt4b/ttam-enums';

const RED_DOT_NAME = 'OFFLINE_EVENTS_RED_DOT';

const offlineEventsTypeMap = {
  auto: 'auto',
  nonAuto: 'non-auto',
};

const offlineEventsTagConfig = {
  [offlineEventsTypeMap.auto]: {
    type: offlineEventsTypeMap.auto,
    autoTracking: true,
    title: i18n.t('module_creative_track_offline_auto_events_title_plural'),
  },
  [offlineEventsTypeMap.nonAuto]: {
    type: offlineEventsTypeMap.nonAuto,
    autoTracking: false,
    title: i18n.t('module_creative_track_offline_normal_events_title'),
  },
};
export function useCreativeOfflineEvents(opt: {
  createMode: Ref<typeof ACTION_TYPE[keyof typeof ACTION_TYPE]>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { createMode, creativeData, adgroupData } = opt;

  const { isEdit, isCreate } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: adgroupData,
  });
  const { loading, oriOfflineEventsList, loadOfflineEventsList } = useOfflineEventsList();

  const { shouldRedDotShow, clickRedDotItem } = useRedDot({ redDotName: RED_DOT_NAME });

  const _trackingOfflineEventSetIds = computed(() => creativeData.value?.trackingOfflineEventSetIds || []);

  // 需要默认选择的事件id列表
  const serverTrackingOfflineEventSetIds = computed(
    () => creativeData.value?.trackingOfflineEventConfig?.serverTrackingOfflineEventSetIds || [],
  );

  const deletedEventsIds = computed(() => creativeData.value?.trackingOfflineEventConfig?.deletedIds || []);
  const deletedEventsNames = computed(
    () => creativeData.value?.trackingOfflineEventConfig?.deletedNames?.join(', ') ?? '',
  );

  // 编辑场景下有被删除的事件，则给出提示
  const isShowDeletedIdsPrompt = computed(() =>
    isEdit.value || isCreate.value ? Boolean(deletedEventsIds.value?.length) : false,
  );

  // 判断离线事件项是否可关闭，编辑场景下，非默认选中的事件都可以关闭。
  const judgeIsClosableOfflineEventsItem = events => {
    if (events?.autoTracking) {
      return isEdit.value ? !serverTrackingOfflineEventSetIds.value?.includes(events?.id) : false;
    }

    return true;
  };
  // 判断是否要禁用选项，非自动离线事件都不能禁用。在编辑场景下，默认选择的自动离线事件都需要禁用选项，不能取消。
  const judgeIsDisabledOfflineEventsItem = events => {
    if (!events?.autoTracking) {
      return false;
    }

    return isEdit.value ? serverTrackingOfflineEventSetIds.value?.includes(events?.id) : true;
  };
  const offlineEventsList = computed(
    () =>
      oriOfflineEventsList.value?.map(i => ({
        ...i,
        label: i?.name || '',
        hasBadge: Boolean(i?.autoTracking),
        closable: judgeIsClosableOfflineEventsItem(i),
        disabled: judgeIsDisabledOfflineEventsItem(i),
      })) || [],
  );
  // 生成标签列表项
  const getLabelNodeById = type => {
    const labelMap = offlineEventsTagConfig[type] || { autoTracking: false };

    return offlineEventsList.value.filter(
      item => _trackingOfflineEventSetIds.value?.includes(item?.id) && item?.autoTracking === labelMap?.autoTracking,
    );
  };

  const generateTagsListItem = item => ({
    title: item?.title || '',
    autoTracking: item?.autoTracking || false,
    list: getLabelNodeById(item?.type) || [],
  });
  // 被选中的列表
  const selectedList = computed(() =>
    Object.keys(offlineEventsTagConfig).map(v => generateTagsListItem(offlineEventsTagConfig[v])),
  );
  return {
    loading,
    selectedList,
    deletedEventsIds,
    deletedEventsNames,
    shouldRedDotShow,
    offlineEventsList,
    isShowDeletedIdsPrompt,
    clickRedDotItem,
    loadOfflineEventsList,
  };
}
